'use client';

import { cn } from '@/shared/lib/cn';
import type { BrandSelection } from '@/types/brand';

interface BrandSelectionSummaryProps {
  /** The picks this row is responsible for, in pick order. */
  entries: BrandSelection[];
  /** Shown when nothing under this row is picked. Omit to render nothing. */
  emptyLabel?: string;
  /** Colour chips, for the surfaces that have room for them (the root row). */
  showSwatches?: boolean;
  className?: string;
}

/**
 * What is picked beneath a row, printed in full.
 *
 * Every level of the Brands picker shows the selection made under it, and the
 * one thing that must not happen is an ellipsis: manufacturers name products at
 * whatever length they like ("Cedar Impressions Double 7" Straight Edge Rough
 * Split Shakes"), and a user can hold five picks from different lines at once.
 * A comma-joined line in a trailing slot showed the first name and half of the
 * second, which is exactly the information that tells the picks apart.
 *
 * The picks are printed as a set, not a ranking: the user cannot reorder them,
 * so numbering them would promise a placement rule the render does not follow.
 *
 * So: one pick per line, wrapping, with its context underneath. Callers place
 * this BELOW the row title (`stackedContent` / a multiline `PickerRow`), never
 * in the trailing value slot that shares the title's line.
 */
export function BrandSelectionSummary({
  entries,
  emptyLabel,
  showSwatches = false,
  className,
}: BrandSelectionSummaryProps) {
  if (entries.length === 0) {
    return emptyLabel ? (
      <span className="text-list-item-subtitle text-muted-foreground">
        {emptyLabel}
      </span>
    ) : null;
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-start gap-2 min-w-0">
          {showSwatches && <SummarySwatch hex={entry.attributes.hex} />}
          <div className="min-w-0">
            <p className="text-list-item-subtitle text-foreground break-words">
              {entry.attributes.title}
            </p>
            {entry.attributes.subtitle && (
              <p className="text-card-meta text-muted-foreground break-words">
                {entry.attributes.subtitle}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A colour value the manufacturer never published is drawn as an empty dashed
 * square, not a grey fill — the same refusal the picker itself makes.
 */
function SummarySwatch({ hex }: { hex: string | null }) {
  if (!hex) {
    return (
      <span
        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-[3px] border border-dashed border-muted-foreground/60"
        style={{
          backgroundImage:
            'linear-gradient(45deg, transparent 45%, currentColor 45%, currentColor 55%, transparent 55%)',
          color: 'color-mix(in oklch, currentColor 35%, transparent)',
        }}
      />
    );
  }

  return (
    <span
      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-[3px] border border-muted-foreground/30"
      style={{ backgroundColor: hex }}
    />
  );
}
